import * as React from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  Dialog,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Autocomplete from "@mui/material/Autocomplete";
import { supabase } from "../logic/client";
import { useI18n } from "../i18n/I18nProvider";

function toDateInputValue(value: string | Date | null | undefined) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function todayDateInputValue() {
  return toDateInputValue(new Date());
}

function isDateBeforeToday(value: string | Date | null | undefined) {
  const dateValue = toDateInputValue(value);
  return !!dateValue && dateValue < todayDateInputValue();
}

function compareWorkshopDates(a: string | Date | null | undefined, b: string | Date | null | undefined) {
  return toDateInputValue(a).localeCompare(toDateInputValue(b));
}


export default function WorkshopManagement(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const { lang } = useI18n();
  const L = (en: string, de: string) => (lang === "de" ? de : en);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const [workshops, setWorkshops] = React.useState<any[]>([]);
  const [employees, setEmployees] = React.useState<any[]>([]);

  const [expandedWorkshop, setExpandedWorkshop] = React.useState<string | null>(null); // NEW

  const [selectedWorkshop, setSelectedWorkshop] = React.useState<any | null>(null);
  const [search, setSearch] = React.useState("");
  const [showPast, setShowPast] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<any | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<any | null>(null);
  const [selectedEmployee, setSelectedEmployee] = React.useState<any | null>(null);

  const [title, setTitle] = React.useState("");
  const [date, setDate] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleSupabaseError = React.useCallback((context: string, error: any) => {
    if (!error) return false;
    const message = error.message || String(error);
    console.error(`${context}:`, error);
    setErrorMessage(message);
    return true;
  }, []);

  const loadData = React.useCallback(async () => {
    setErrorMessage(null);

    const { data, error } = await supabase.rpc("get_workshops_with_participants");
    if (handleSupabaseError("Loading workshops failed", error)) return;
    setWorkshops(data || []);

    const { data: emp, error: empError } = await supabase
      .from("employees")
      .select("id, first_name, last_name, company_email");
    if (handleSupabaseError("Loading employees failed", empError)) return;

    setEmployees(emp || []);
  }, [handleSupabaseError]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    await supabase.from("workshops").delete().eq("id", deleteTarget.id);
    setDeleteTarget(null);
    loadData();
  }

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  async function createWorkshop() {
    if (!title.trim() || !date) {
      setErrorMessage(L("Please enter a title and date.", "Bitte Titel und Datum eingeben."));
      return;
    }

    if (isDateBeforeToday(date)) {
      setErrorMessage(L("Please choose today or a future date.", "Bitte wähle heute oder ein zukünftiges Datum."));
      return;
    }

    const { error } = await supabase.rpc("create_workshop", {
      p_title: title,
      p_date: date,
    });
    if (handleSupabaseError("Creating workshop failed", error)) return;

    setTitle("");
    setDate("");
    loadData();
  }

  async function addParticipant(wid: string, eid: string) {
    const { error } = await supabase.rpc("add_participant", {
      p_workshop: wid,
      p_employee: eid,
    });
    if (handleSupabaseError("Adding participant failed", error)) return;

    const { data, error: loadError } = await supabase.rpc("get_workshops_with_participants");
    if (handleSupabaseError("Reloading workshops failed", loadError)) return;
    setWorkshops(data || []);

    // update currently open workshop
    const updated = data?.find((w: any) => w.id === wid);
    if (updated) {
      setSelectedWorkshop(updated);
    }
  }

  async function removeParticipant(wid: string, eid: string) {
    const { error } = await supabase.rpc("remove_participant", {
      p_workshop: wid,
      p_employee: eid,
    });
    if (handleSupabaseError("Removing participant failed", error)) return;
    
    const { data, error: loadError } = await supabase.rpc("get_workshops_with_participants");
    if (handleSupabaseError("Reloading workshops failed", loadError)) return;
    setWorkshops(data || []);

    // update currently open workshop
    const updated = data?.find((w: any) => w.id === wid);
    if (updated) {
      setSelectedWorkshop(updated);
    }
  }

  async function activateWorkshop(wid: string) {
    // 1. deactivate all
    await supabase
      .from("workshops")
      .update({ is_active: false })
      .neq("id", wid);

    // 2. activate selected
    const { error } = await supabase
      .from("workshops")
      .update({ is_active: true })
      .eq("id", wid);

    if (error) {
      console.error("Activation failed:", error);
    }

    await loadData();
  }

  async function startWorkshop(wid: string) {
    // 1. stop all
    await supabase
      .from("workshops")
      .update({ has_started: false })
      .neq("id", wid);

    // 2. start selected
    const { error } = await supabase
      .from("workshops")
      .update({ has_started: true })
      .eq("id", wid);

    if (error) {
      console.error("Starting workshop failed:", error);
    }

    setWorkshops((prev) =>
      prev.map((w) =>
        w.id === wid
          ? { ...w, has_started: true }
          : { ...w, has_started: false }
      )
    );
  }

  async function stopWorkshop(wid: string) {
    // stop selected
    const { error } = await supabase
      .from("workshops")
      .update({ has_started: false })
      .eq("id", wid);

    if (error) {
      console.error("Stopping workshop failed:", error);
    }

    setWorkshops((prev) =>
      prev.map((w) =>
        w.id === wid ? { ...w, has_started: false } : w
      )
    );
  }

  const active = workshops.find((w) => w.is_active);
  const past = workshops.filter((w) => isDateBeforeToday(w.date) && !w.is_active);
  const future = workshops.filter((w) => !isDateBeforeToday(w.date) && !w.is_active);

  const ordered = [
    ...(active ? [active] : []),
    ...future.sort((a, b) => compareWorkshopDates(a.date, b.date)),
    ...(showPast ? past.sort((a, b) => compareWorkshopDates(b.date, a.date)) : []),
  ];

  function isWorkshopPast(w: any) {
    if (!w?.date) return false;
    return isDateBeforeToday(w.date);
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, sm: 3, md: 4 },
        position: "relative",
        background: "linear-gradient(180deg,#0b0b0c,#140909)",
      }}
    >
      {/* BACK BUTTON */}
      <Box
        sx={{
          position: "absolute",
          top: isTablet ? 12 : 24,
          left: isTablet ? 12 : 24,
          zIndex: 10,
        }}
      >
        <IconButton
          onClick={() => handlePageChange("ControlPanel")}
          sx={{
            color: "white",
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 0 15px rgba(255,255,255,0.1)",
            "&:hover": {
              background: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>
      <Stack spacing={4} maxWidth={1000} mx="auto" sx={{ pt: { xs: 5, sm: 0 } }}>
        <Typography sx={{ color: "#fff", fontSize: { xs: 22, sm: 26, md: 30 }, fontWeight: 900, lineHeight: 1.2 }}>
          {L("Workshop & Participant Management", "Workshop & Teilnehmer Verwaltung")}
        </Typography>

        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        {/* CREATE */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, bgcolor: "#cecececb" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              sx={{
                "& input": {
                  fontSize: 15,
                  padding: "10px 12px",
                },
              }}
            />
            <TextField
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              inputProps={{ min: todayDateInputValue() }}
              sx={{
                minWidth: 170, // prevents cutoff
                "& input": {
                  fontSize: 15,
                  padding: "10px 12px",
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Button
              onClick={createWorkshop}
              sx={{
                background: "#ff1f1f",
                color: "#fff",
                fontWeight: 800,
                px: 3,
              }}
            >
              {L("Create", "Erstellen")}
            </Button>
          </Stack>
        </Paper>

        {/* PAST TOGGLE */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography
                onClick={() => setShowPast((p) => !p)}
                sx={{
                color: "#888",
                cursor: "pointer",
                fontSize: 13,
                textAlign: "center",
                transition: "0.2s",
                "&:hover": { color: "#fff" },
                }}
            >
                {showPast
                ? L("Hide past workshops", "Vergangene Workshops ausblenden")
                : L("Check past workshops", "Vergangene Workshops anzeigen")}
            </Typography>
        </Box>

        {/* WORKSHOPS */}
        {ordered.map((w) => {
          const isOpen = expandedWorkshop === w.id;
          //const isPast = new Date(w.date) < now;

          return (
            <Paper
              key={w.id}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: w.is_active ? "#524040" : "#0f0f0f",
                border: w.is_active ? "1px solid #ffffff10" : "1px solid #ffffff10",
              }}
            >
              <Stack spacing={2}>

                {/* HEADER */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>

                  {/* LEFT SIDE */}
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <IconButton
                        onClick={() => setExpandedWorkshop(isOpen ? null : w.id)}
                        sx={{ color: "#fff" }}
                      >
                        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box>
                          <Typography sx={{ color: "#fff", fontWeight: 800 }}>
                            {w.title}
                          </Typography>
                          <Typography sx={{ color: "#aaa", fontSize: 13 }}>
                            {w.date}
                          </Typography>
                        </Box>

                        {/* STATUS CHIP */}
                        {w.is_active ? (
                          <Chip
                            label="ACTIVE"
                            size="small"
                            sx={{
                              background: "#e40505",
                              color: "#fff",
                              fontWeight: 700,
                              height: 22,
                            }}
                          />
                        ) : isWorkshopPast(w) ? (
                          <Chip
                            label="INACTIVE"
                            size="small"
                            sx={{
                              background: "#1a1a1a",
                              color: "#666",
                              fontWeight: 700,
                              height: 22,
                            }}
                          />
                        ) : (
                          <Chip
                            label="ACTIVATE"
                            size="small"
                            onClick={() => activateWorkshop(w.id)}
                            sx={{
                              background: "#2a2a2a",
                              color: "#aaa",
                              fontWeight: 700,
                              height: 22,
                              cursor: "pointer",
                              "&:hover": {
                                background: "#3a3a3a",
                                color: "#fff",
                              },
                            }}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Stack>

                  {/* RIGHT SIDE */}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {w.is_active && !w.has_started && (
                      <Button
                        onClick={() => startWorkshop(w.id)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          px: 2,
                          background: "#00c853",
                          color: "#fff",
                          "&:hover": {
                            background: "#00e676",
                          },
                        }}
                      >
                        {L("Start workshop", "Workshop starten")}
                      </Button>
                    )}

                    {w.is_active && w.has_started && (
                      <Button
                        onClick={() => stopWorkshop(w.id)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          px: 2,
                          background: "#d50000",
                          color: "#fff",
                          "&:hover": {
                            background: "#ff1744",
                          },
                        }}
                      >
                        {L("Stop workshop", "Workshop stoppen")}
                      </Button>
                    )}
                    <Button
                      onClick={() => setSelectedWorkshop(w)}
                      variant="outlined"
                      sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: "#ffffff33",
                            color: "#fff",
                            px: 2,
                            "&:hover": {
                            borderColor: "#f8e8e8",
                            background: "#cacaca22",
                            },
                        }}
                    >
                      {isWorkshopPast(w)
                        ? L("Check participants", "Teilnehmer ansehen")
                        : L("Manage participants", "Teilnehmer verwalten")}
                    </Button>

                    <Button
                      onClick={() => setDeleteTarget(w)}
                      variant="outlined"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#ff4d4d55",
                        color: "#ff4d4d",
                        "&:hover": {
                        borderColor: "#ff4d4d",
                        background: "#ff4d4d22",
                        },
                    }}
                    >
                      {L("DELETE", "LÖSCHEN")}
                    </Button>
                  </Stack>
                </Stack>

                {/* EXPANDED CONTENT */}
                {isOpen && (
                  <Stack spacing={2} sx={{ pt: 1 }}>

                    {/* DATE EDIT */}
                    {!isWorkshopPast(w) && (
                      <TextField
                        type="date"
                        value={w.date}
                        onChange={async (e) => {
                          if (isDateBeforeToday(e.target.value)) {
                            setErrorMessage(L("Please choose today or a future date.", "Bitte wähle heute oder ein zukünftiges Datum."));
                            return;
                          }

                          await supabase
                            .from("workshops")
                            .update({ workshop_date: e.target.value })
                            .eq("id", w.id);
                          loadData();
                        }}
                        inputProps={{ min: todayDateInputValue() }}
                        size="small"
                        sx={{ maxWidth: 180, bgcolor: "#fff", borderRadius: 2 }}
                      />
                    )}

                    {(() => {
                      const includedModules = [...(w.modules || [])].sort(
                        (a: any, b: any) => (a.order_index ?? 999) - (b.order_index ?? 999)
                      );

                      return (
                        <Stack spacing={1}>
                          <Typography sx={{ color: "#aaa", fontSize: 12 }}>
                            {L("Included modules", "Enthaltene Module")}
                          </Typography>

                          {includedModules.length === 0 ? (
                            <Typography sx={{ color: "#fff", fontSize: 13, opacity: 0.8 }}>
                              {L(
                                "The 9 workshop modules will be added automatically.",
                                "Die 9 Workshop-Module werden automatisch hinzugefügt."
                              )}
                            </Typography>
                          ) : (
                            includedModules.map((m: any) => (
                              <Stack
                                key={m.id}
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{
                                  px: 2,
                                  py: 1,
                                  borderRadius: 2,
                                  bgcolor: "#2a2a2a",
                                  "&:hover": {
                                    bgcolor: "#383838",
                                  },
                                }}
                              >
                                <Chip
                                  label={m.order_index ?? "-"}
                                  size="small"
                                  sx={{
                                    minWidth: 32,
                                    bgcolor: "#ff1f1f",
                                    color: "#fff",
                                    fontWeight: 800,
                                  }}
                                />
                                <Typography sx={{ color: "#fff", fontSize: 13 }}>
                                  {lang === "de" ? m.name_de || m.name : m.name_en || m.name}
                                </Typography>
                              </Stack>
                            ))
                          )}
                        </Stack>
                      );
                    })()}
                  </Stack>
                )}

              </Stack>
            </Paper>
          );
        })}
        {/* PARTICIPANT DIALOG */}
        <Dialog open={!!selectedWorkshop} fullWidth maxWidth="sm">
            <Box sx={{ p: 3, bgcolor: "#754848" }}>
                <Stack spacing={3}>

                {/* HEADER */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: 18 }}>
                    {selectedWorkshop?.title}
                    </Typography>

                    <IconButton onClick={() => setSelectedWorkshop(null)}>
                    <CloseIcon sx={{ color: "#ffffff" }} />
                    </IconButton>
                </Stack>

                {/* ADD SECTION */}
                {!isWorkshopPast(selectedWorkshop) && (
                  <Box
                      sx={{
                      p: 1,
                      borderRadius: 3,
                      bgcolor: "#ffffff91",
                      border: "1px solid #ffffff",
                      }}
                  >
                      <Stack spacing={2}>
                      <Typography sx={{ color: "#000000", fontSize: 13 }}>
                          {L("Add participant", "Teilnehmer hinzufügen")}
                      </Typography>

                      <Stack direction="row" spacing={2}>
                          <Autocomplete
                          fullWidth
                          value={selectedEmployee}
                          onChange={(_, value) => setSelectedEmployee(value)}
                          options={employees.filter(
                              (e) =>
                              !selectedWorkshop?.participants?.some(
                                  (p: any) => p.employee_id === e.id
                              )
                          )}
                          getOptionLabel={(e) =>
                              `${e.company_email}`
                          }
                          renderInput={(params) => (
                              <TextField
                              {...params}
                              placeholder={L(
                                  "Search employee...",
                                  "Mitarbeiter suchen..."
                              )}
                              />
                          )}
                          />

                          <Button
                          disabled={!selectedEmployee}
                          onClick={async () => {
                              await addParticipant(
                                selectedWorkshop.id,
                                selectedEmployee.id
                              );
                              setSelectedEmployee(null);
                          }}
                          sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              px: 3,
                              background: "#ff1f1f",
                              color: "#ffffff",
                              opacity: selectedEmployee ? 1 : 0.6,
                              "&:hover": {
                              background: "#ff3b3b",
                              },
                          }}
                          >
                          {L("Add", "Hinzufügen")}
                          </Button>
                      </Stack>
                      </Stack>
                  </Box>
                )}

                {/* PARTICIPANTS LIST */}
                <Box
                  sx={{
                    borderRadius: 3,
                    bgcolor: "#121212",
                    border: "1px solid #ffffff10",
                    overflow: "hidden",
                  }}
                >
                  <Stack>

                    {/* HEADER */}
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #ffffff10",
                        bgcolor: "#1a1a1a",
                      }}
                    >
                      <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 500 }}>
                        {L("Participants", "Teilnehmer")}
                      </Typography>
                    </Box>

                    {/* SEARCH BAR */}
                    <Box
                      sx={{
                        p: 1.5,
                        borderBottom: "1px solid #ffffff10",
                        bgcolor: "#141414",
                      }}
                    >
                      <TextField
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={L("Search participant...", "Teilnehmer suchen...")}
                        size="small"
                        sx={{
                          bgcolor: "#ffffff",
                          borderRadius: 2,
                        }}
                      />
                    </Box>

                    {/* LIST */}
                    <Box
                      sx={{
                        maxHeight: 300,
                        overflowY: "auto",
                        p: 1,
                      }}
                    >
                      <Stack spacing={1}>
                        {selectedWorkshop?.participants
                          ?.filter((p: any) =>
                            p.email?.toLowerCase().includes(search.toLowerCase())
                          )
                          .map((p: any) => (
                            <Box
                              key={p.employee_id}
                              sx={{
                                px: 2,
                                py: 1.5,
                                borderRadius: 2,
                                bgcolor: "#2a2a2a",
                                transition: "0.2s",
                                "&:hover": {
                                  bgcolor: "#383838",
                                },
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  sx={{
                                    color: "#fff",
                                    fontSize: 14,
                                    wordBreak: "break-all", // important for email on tablet
                                  }}
                                >
                                  {p.email}
                                </Typography>
                                {!isWorkshopPast(selectedWorkshop) && (
                                  <IconButton
                                    onClick={() =>
                                      setRemoveTarget({
                                        workshopId: selectedWorkshop.id,
                                        employeeId: p.employee_id,
                                        name: p.email,
                                      })
                                    }
                                    sx={{
                                      color: "#ff4d4d",
                                      ml: 1,
                                      "&:hover": {
                                        background: "#ff4d4d22",
                                      },
                                    }}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                )}
                              </Stack>
                            </Box>
                          ))}
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
                </Stack>
            </Box>
            </Dialog>

        <Dialog open={!!deleteTarget} maxWidth="xs">
            <Box sx={{ p: 3, bgcolor: "#3e1a1a" }}>
                <Stack spacing={3}>
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>
                    {L("Delete workshop?", "Workshop löschen?")}
                </Typography>

                <Typography sx={{ color: "#aaa", fontSize: 14 }}>
                    {L(
                    `Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`,
                    `Möchtest du "${deleteTarget?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
                    )}
                </Typography>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                    onClick={() => setDeleteTarget(null)}
                    variant="outlined"
                    sx={{
                        textTransform: "none",
                        borderColor: "#ffffff33",
                        color: "#fff",
                    }}
                    >
                    {L("Cancel", "Abbrechen")}
                    </Button>

                    <Button
                    onClick={confirmDelete}
                    sx={{
                        textTransform: "none",
                        background: "#ff1f1f",
                        color: "#fff",
                        fontWeight: 700,
                        "&:hover": {
                        background: "#ff3b3b",
                        },
                    }}
                    >
                    {L("Yes, delete", "Ja, löschen")}
                    </Button>
                </Stack>
                </Stack>
            </Box>
        </Dialog>

        <Dialog open={!!removeTarget} maxWidth="xs">
            <Box sx={{ p: 3, bgcolor: "#2a1111" }}>
                <Stack spacing={3}>
                <Typography sx={{ color: "#fff", fontWeight: 800 }}>
                    {L("Remove participant?", "Teilnehmer entfernen?")}
                </Typography>

                <Typography sx={{ color: "#aaa", fontSize: 14 }}>
                    {L(
                    `Remove "${removeTarget?.name}" from this workshop?`,
                    `"${removeTarget?.name}" aus diesem Workshop entfernen?`
                    )}
                </Typography>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                    onClick={() => setRemoveTarget(null)}
                    variant="outlined"
                    sx={{
                        textTransform: "none",
                        borderColor: "#ffffff33",
                        color: "#fff",
                    }}
                    >
                    {L("Cancel", "Abbrechen")}
                    </Button>

                    <Button
                    onClick={async () => {
                        await removeParticipant(
                        removeTarget.workshopId,
                        removeTarget.employeeId
                        );
                        setRemoveTarget(null);
                    }}
                    sx={{
                        textTransform: "none",
                        background: "#ff1f1f",
                        color: "#fff",
                        fontWeight: 700,
                    }}
                    >
                    {L("Yes, remove", "Ja, entfernen")}
                    </Button>
                </Stack>
                </Stack>
            </Box>
            </Dialog>
      </Stack>
    </Box>
  );
}
